import { useEffect, useId, useRef, useState } from 'react';

import { PASSWORD_REVEAL_PADDING, PasswordReveal } from '@shared/ui/PasswordReveal';
import { Dialog, isCancelledSubmit } from '@shared/ui/dialog';

import { copy } from './copy';

/**
 * Rotation. **The current value is not shown here or anywhere else**, and the
 * new one is replaced rather than revealed.
 *
 * Three properties make that true rather than merely intended:
 *
 * - **Nothing about the existing secret is in this dialog.** Not the value, not
 *   a truncation of it, not a mask of it — the payload carries no `value` field
 *   for a secret key at all, so there is nothing to render even by accident.
 * - **The new value never enters React state.** It is read from an uncontrolled
 *   input at the moment of the write, so no render holds it, no attribute
 *   carries it, and no error boundary can serialise it into a report.
 * - **The field is cleared on close whether the rotation committed or was
 *   cancelled.** A secret left in an element's `value` is readable from the
 *   browser's own tools after the dialog closed over it.
 *
 * The commit is `danger`-styled because rotation **invalidates the standing
 * value and cannot be undone with it** — every caller still carrying the old one
 * stops working until it is updated, and the hint says so before the press
 * rather than after.
 *
 * This is the same shape and the same rule the payment-gateway surface ships,
 * deliberately: one action, one form, one area.
 */
export function RotateDialog({
  open,
  configurationKey,
  busy,
  formError,
  onCommit,
  onCancel,
}: {
  open: boolean;
  configurationKey: string | null;
  busy: boolean;
  formError: string | null;
  /** Receives the pasted value once and never sees it again. */
  onCommit: (value: string) => void;
  onCancel: () => void;
}) {
  const headingId = useId();
  const fieldId = useId();
  const secretRef = useRef<HTMLInputElement>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);

  /** One clear, on every open and on every close. */
  function clearSecret() {
    if (secretRef.current) secretRef.current.value = '';
    setFieldError(null);
  }

  useEffect(() => {
    if (open) clearSecret();
  }, [open]);

  if (!configurationKey) return null;

  return (
    <Dialog
      open={open}
      labelledBy={headingId}
      onClose={(returnValue) => {
        clearSecret();
        if (returnValue !== 'rotate') onCancel();
      }}
    >
      <form
        method="dialog"
        onSubmit={(event) => {
          // «ألغِ» is a submit too — see `isCancelledSubmit`. Left alone, it lets
          // `method="dialog"` close the element, so an operator who opened the
          // rotation and thought better of it does not rotate the secret on the
          // way out. The empty-field guard below never ran on that path: the
          // exit carries `formNoValidate` and the field is untouched, so the
          // dialog closed and the write went with it.
          if (isCancelledSubmit(event, 'rotate')) return;
          event.preventDefault();

          const pasted = secretRef.current?.value.trim() ?? '';
          if (pasted === '') {
            setFieldError(copy.rotate.required);
            return;
          }

          setFieldError(null);
          onCommit(pasted);
          // The value leaves the element in the same tick it was read; the
          // caller closes the dialog once the write lands.
          clearSecret();
        }}
        className="flex flex-col gap-4"
      >
        <div>
          <h2 id={headingId} className="text-h3">
            {copy.rotate.heading(configurationKey)}
          </h2>
          <p className="mb-0 mt-1 max-w-none text-sm text-ink-muted">{copy.rotate.body}</p>
        </div>

        {formError ? (
          <p role="alert" className="mb-0 max-w-none text-sm text-danger">
            {formError}
          </p>
        ) : null}

        <div className="grid gap-2">
          <label htmlFor={fieldId} className="text-sm font-medium text-ink">
            {copy.rotate.valueLabel}
          </label>
          {/*
            A secret being written is the one password the operator CAN check
            against what they were handed, so the reveal earns its place here —
            a mistyped gateway key fails at the provider, hours later, with a
            message that names nothing.
          */}
          <PasswordReveal>
            {(type) => (
              <input
                ref={secretRef}
                id={fieldId}
                type={type}
                dir="ltr"
                autoComplete="off"
                spellCheck={false}
                required
                aria-required
                aria-invalid={Boolean(fieldError)}
                className={`mono hit-target w-full rounded-sm border border-border-strong bg-surface px-3 text-sm text-ink ${PASSWORD_REVEAL_PADDING}`}
              />
            )}
          </PasswordReveal>
          <p className="mb-0 max-w-none text-xs text-ink-muted">{copy.rotate.valueHint}</p>
          {fieldError ? <p className="mb-0 max-w-none text-xs text-danger">{fieldError}</p> : null}
        </div>

        <div className="flex flex-wrap gap-2">
          {/*
            The field above is `required`, so without `formNoValidate` the
            browser would block the **exit** and demand a secret from an operator
            who has just decided not to rotate one.
          */}
          <button
            type="submit"
            value="cancel"
            formNoValidate
            onClick={onCancel}
            className="rounded-sm border border-border-strong px-5 py-3 text-sm text-ink hover:bg-hover-bg"
          >
            {copy.dialog.cancel}
          </button>
          <button
            type="submit"
            value="rotate"
            disabled={busy}
            className="rounded-sm border border-danger px-5 py-3 text-sm text-danger hover:bg-danger-soft disabled:cursor-not-allowed disabled:opacity-45"
          >
            {copy.rotate.commit}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
